import { Routes, Route, Navigate } from "react-router-dom";
import { lazy, useEffect, useState } from "react";
import { MainLayout } from "../pages/main";
import { AUTH_ROUTES, MAIN_ROUTES, getAbsolute } from "../constants/routes";
import { WithSuspense } from "../components/SuspenseWidget";
import { useDispatch, useSelector } from "react-redux";
import { loadUser } from "../store/auth/authAction";
import { LoadingModal } from "../components/LoadingModal";
import { getUserPermissions } from "../util/Permissions.ts";
import { PermissionProvider, usePermission } from "../context/PermissionContext";
import PermissionRoute from "../components/Permission/PermissionRoute";
import {
  getFirstAccessibleRoute,
  getFirstAccessibleRouteInSection,
} from "../util/General.js";

const SignInScreen = lazy(() => import("../pages/auth/sign-in"));
const ResetPassScreen = lazy(() => import("../pages/auth/reset-pass"));
const ChangePassScreen = lazy(() => import("../pages/auth/change-pass"));
const DashboardScreen = lazy(() => import("../pages/main/dashboard"));
const UserAndPermissionsScreen = lazy(() =>
  import("../pages/main/user-permission")
);

const UsersScreen = lazy(() => import("../pages/main/master-management/users"));

const WebsiteHeading = lazy(() =>
  import("../pages/main/dealer/new-dealer/website_heading/")
);

const ViewUser = lazy(() =>
  import("../pages/main/master-management/users/view-user")
);
const NewUserScreen = lazy(() =>
  import("../pages/main/master-management/users/new-user")
);
const RolesScreen = lazy(() => import("../pages/main/master-management/roles"));
const ViewRole = lazy(() =>
  import("../pages/main/master-management/roles/view-role")
);
const NewRoleScreen = lazy(() =>
  import("../pages/main/master-management/roles/new-role")
);
const DealersScreen = lazy(() => import("../pages/main/dealer"));

const DealersSubscription = lazy(() => import("../pages/main/dealer/dealerSubscription"));

const AddDealerSubscription = lazy(() => import("../pages/main/dealer/dealerSubscription/addSubscription"));

const DealerSubScreen = lazy(() =>
  import("../pages/main/dealer/dealerSubUser")
);
const NewDealerSubScreen = lazy(() =>
  import("../pages/main/dealer/dealerSubUser/new")
);
const ViewDealerSubScreen = lazy(() =>
  import("../pages/main/dealer/dealerSubUser/view")
);
const EditDealerSubScreen = lazy(() =>
  import("../pages/main/dealer/dealerSubUser/edit/index.jsx")
);

const PrivacyPolicyScreen = lazy(() =>
  import("../pages/main/static-pages/privacy")
);
const TermsConditionsScreen = lazy(() =>
  import("../pages/main/static-pages/t&c")
);
const LoanLenderScreen = lazy(() =>
  import("../pages/main/static-pages/loan-lender")
);
const InsuranceLenderScreen = lazy(() =>
  import("../pages/main/static-pages/insurance-lender")
);
const NewDealerScreen = lazy(() => import("../pages/main/dealer/new-dealer"));
const DealerSetting = lazy(() => import("../pages/main/dealer/dealerSetting"));
const DealerWhatsAppChat = lazy(() =>
  import("../pages/main/dealer/whatsapp-chat")
);
const ViewDealerScreen = lazy(() => import("../pages/main/dealer/view-dealer"));

const ServiceHistory = lazy(() =>
  import("../pages/main/services/ServiceHistory/")
);
const NewService = lazy(() =>
  import("../pages/main/services/ServiceHistory/new")
);
const ViewService = lazy(() =>
  import("../pages/main/services/ServiceHistory/view")
);
const EditService = lazy(() =>
  import("../pages/main/services/ServiceHistory/edit")
);

const VehicleCrime = lazy(() => import("../pages/main/services/VehicleCrime/"));
const NewVehicleCrime = lazy(() =>
  import("../pages/main/services/VehicleCrime/new")
);
const ViewVehicleCrime = lazy(() =>
  import("../pages/main/services/VehicleCrime/view")
);
const EditVehicleCrime = lazy(() =>
  import("../pages/main/services/VehicleCrime/edit")
);

const UploadDealer = lazy(() =>
  import("../pages/main/dealer/UploadDocument/new")
);
const UploadDelaersDocument = lazy(() =>
  import("../pages/main/dealer/UploadDocument/upload/")
);
const BulkOnboardDealer = lazy(() =>
  import("../pages/main/dealer/bulk-onboard")
);
const BulkAssignDealer = lazy(() =>
  import("../pages/main/dealer/bulk-assign")
);

const PickupRequestsScreen = lazy(() =>
  import("../pages/main/services/DoorServices")
);
const AddDoorService = lazy(() =>
  import("../pages/main/services/DoorServices/new")
);
const EditDoorService = lazy(() =>
  import("../pages/main/services/DoorServices/edit")
);
const ViewDoorService = lazy(() =>
  import("../pages/main/services/DoorServices/view")
);

const RTOCasesScreen = lazy(() => import("../pages/main/services/RTOCases"));
const NewRTOCasesScreen = lazy(() =>
  import("../pages/main/services/RTOCases/Form")
);
const ViewRTOCasesScreen = lazy(() =>
  import("../pages/main/services/RTOCases/Form")
);
const EditRTOCasesScreen = lazy(() =>
  import("../pages/main/services/RTOCases/Form")
);

const ChallanList = lazy(() => import("../pages/main/services/Challan"));

const NewChallanRequest = lazy(() =>
  import("../pages/main/services/Challan/new-request")
);
const NewChallan = lazy(() => import("../pages/main/services/Challan/new"));
const EditChallan = lazy(() => import("../pages/main/services/Challan/edit"));
const ViewChallan = lazy(() => import("../pages/main/services/Challan/view"));

//
const NewDMScreen = lazy(() =>
  import("../pages/main/services/DigitalMarketing/new")
);
const ListDMScreen = lazy(() =>
  import("../pages/main/services/DigitalMarketing")
);
const EditDMScreen = lazy(() =>
  import("../pages/main/services/DigitalMarketing/edit")
);
const ViewDMScreen = lazy(() =>
  import("../pages/main/services/DigitalMarketing/view")
);

// const TransactionScreen = lazy(() => import("../pages/main/transaction"));
const BankTransactionScreen = lazy(() =>
  import("../pages/main/transaction/bank-transaction")
);
const AddBankTransactionScreen = lazy(() =>
  import("../pages/main/transaction/bank-transaction/new")
);
const OutstandingTransactionScreen = lazy(() =>
  import("../pages/main/transaction/outstanding-transaction")
);
const ServiceTransactionScreen = lazy(() =>
  import("../pages/main/transaction/service-transaction")
);

const InventoryScreen = lazy(() => import("../pages/main/inventory"));
const LeadListScreen = lazy(() => import("../pages/main/lead"));
const EditCarDetails = lazy(() =>
  import("../pages/main/edit-car-details/EditCarDetails.jsx")
);

const UpdateCarImages = lazy(() =>
  import("../pages/main/edit-car-details/UpdateCarImages.jsx")
);

//Customer-Lead-Management
const CustomerLead = lazy(() => import("../pages/main/lead/customer-lead"));
const CustomerPreferences = lazy(() =>
  import("../pages/main/lead/customer-preferences")
);
const AssignDealers = lazy(() =>
  import("../pages/main/lead/customer-preferences/step2")
);
const CallLogDashboard = lazy(() =>
  import("../pages/main/dealer/call-log-dashboard")
);

const InventoryListScreen = lazy(() =>
  import("../pages/main/inventory/inventory-list")
);

const NewInventoryRequest = lazy(() =>
  import("../pages/main/inventory/inventory-list/newInventory")
);
const PhotoShootScreen = lazy(() =>
  import("../pages/main/services/Photoshoot")
);
const AddPhotoShootScreen = lazy(() =>
  import("../pages/main/services/Photoshoot/new")
);
const ViewPhotoShootScreen = lazy(() =>
  import("../pages/main/services/Photoshoot/view")
);
const EditPhotoShootScreen = lazy(() =>
  import("../pages/main/services/Photoshoot/edit")
);

const SocialMediaScreen = lazy(() =>
  import("../pages/main/services/SocialMedia")
);
const AddSocialMediaScreen = lazy(() =>
  import("../pages/main/services/SocialMedia/new")
);
const ViewSocialMediaScreen = lazy(() =>
  import("../pages/main/services/SocialMedia/view")
);
const EditSocialMediaScreen = lazy(() =>
  import("../pages/main/services/SocialMedia/edit")
);

const FinancialServicesScreen = lazy(() =>
  import("../pages/main/financial-services")
);

const LoansListScreen = lazy(() =>
  import("../pages/main/financial-services/loans")
);

const AddLoanScreen = lazy(() =>
  import("../pages/main/financial-services/loans/new")
);
const EditLoanScreen = lazy(() =>
  import("../pages/main/financial-services/loans/edit")
);
const ViewLoanScreen = lazy(() =>
  import("../pages/main/financial-services/loans/view")
);

const WebsiteDevelopmentScreen = lazy(() =>
  import("../pages/main/services/WebsiteDevelopment")
);
const AddWebsiteDevelopmentScreen = lazy(() =>
  import("../pages/main/services/WebsiteDevelopment/new")
);
const ViewWebsiteDevelopmentScreen = lazy(() =>
  import("../pages/main/services/WebsiteDevelopment/view")
);
const EditWebsiteDevelopmentScreen = lazy(() =>
  import("../pages/main/services/WebsiteDevelopment/edit")
);

//
const InsuranceListScreen = lazy(() =>
  import("../pages/main/financial-services/loanInsurance")
);

const AddInsuranceScreen = lazy(() =>
  import("../pages/main/financial-services/loanInsurance/new")
);
const EditInsuranceScreen = lazy(() =>
  import("../pages/main/financial-services/loanInsurance/edit")
);
const ViewInsuranceScreen = lazy(() =>
  import("../pages/main/financial-services/loanInsurance/view")
);
const CreativeMakingScreen = lazy(() =>
  import("../pages/main/services/CreativeMaking")
);
const AddCreativeMakingScreen = lazy(() =>
  import("../pages/main/services/CreativeMaking/new")
);
const ViewCreativeMakingScreen = lazy(() =>
  import("../pages/main/services/CreativeMaking/view")
);
const EditCreativeMakingScreenScreen = lazy(() =>
  import("../pages/main/services/CreativeMaking/edit")
);
const ViewVahanCheck = lazy(() =>
  import("../pages/main/services/VahanCheck/view")
);

const VahanCheckListScreen = lazy(() =>
  import("../pages/main/services/VahanCheck")
);

const AddVahanCheck = lazy(() =>
  import("../pages/main/services/VahanCheck/add")
);

const ListD2DScreen = lazy(() => import("../pages/main/services/D2DAuction"));
const ViewD2DScreen = lazy(() =>
  import("../pages/main/services/D2DAuction/auctions/view")
);

const ListD2DRequirementScreen = lazy(() =>
  import("../pages/main/services/D2DRequirement")
);
const ViewD2DRequirementScreen = lazy(() =>
  import("../pages/main/services/D2DRequirement/requirements/view")
);

const DealerUserReels = lazy(() =>
  import("../pages/main/services/Reels/index.jsx")
);

const DealerClassifiedListing = lazy(() =>
  import("../pages/main/services/ClassifiedListing/index.jsx")
);

const DealerClassifiedListingEdit = lazy(() =>
  import("../pages/main/services/ClassifiedListing/edit/index.jsx")
);

const DealerClassifiedListingView = lazy(() =>
  import("../pages/main/services/ClassifiedListing/view/index.jsx")
);

const DealerClassifiedListingAdd = lazy(() =>
  import("../pages/main/services/ClassifiedListing/new/index.jsx")
);

const AddDealerServiceImage = lazy(() =>
  import("../pages/main/dealer/dealerServices/add/index.jsx")
);
const ListDealerServiceImage = lazy(() =>
  import("../pages/main/dealer/dealerServices/index.js")
);
const DealerNotificationScreen = lazy(() =>
  import("../pages/main/dealer/notification/index.jsx")
);

/* Wraps a route element with a permission check so direct URL navigation
   can't reach a page the sidebar wouldn't have shown. `module` should match
   the `sub_module_name` returned by the get-user API's `roles` payload. */
const guarded = (module, action, element) => (
  <PermissionRoute module={module} action={action}>
    {element}
  </PermissionRoute>
);

const RTO_CASES_ROUTE = getAbsolute(MAIN_ROUTES.RTO_SERVICES.RTO_CASES);

/* Default-landing redirects that respect the role's actual permissions,
   instead of blindly sending everyone to RTO Consultancy even when they
   have no access to it (which used to show the Unauthorized page). Still
   prefers RTO Consultancy — the app's original default landing page —
   whenever the role has ANY permission on it (not specifically "View" —
   same "any action grants access" rule used everywhere else), so behavior
   is unchanged for roles that can see it. Only deviates when they can't:
   first tries the Services section's other accessible items, then the
   app-wide first accessible route, then Dashboard as a last resort.
   Dashboard is always visible for every role, so a plain "first visible
   nav item" would always resolve to Dashboard and never actually land
   anyone in Services. */
const DefaultRedirect = () => {
  const { hasModuleAccess } = usePermission();
  const target = hasModuleAccess("RTO Consultancy")
    ? RTO_CASES_ROUTE
    : getFirstAccessibleRouteInSection("Services") ||
      getFirstAccessibleRoute() ||
      MAIN_ROUTES.DASHBOARD;
  return <Navigate to={target} replace />;
};

const SectionDefaultRedirect = ({ sectionText, fallback }) => {
  const { hasModuleAccess } = usePermission();
  const target =
    sectionText === "Services" && hasModuleAccess("RTO Consultancy")
      ? RTO_CASES_ROUTE
      : getFirstAccessibleRouteInSection(sectionText) || getFirstAccessibleRoute() || fallback;
  return <Navigate to={target} replace />;
};

export const Router = () => {
  const dispatch = useDispatch();

  const [loadingUser, setLoadingUser] = useState(true);
  const onUserLoadingFinished = () => {
    setLoadingUser(false);
  };

  useEffect(() => {
    dispatch(loadUser(() => onUserLoadingFinished()));
  }, []);

  const { admin, isLoggedIn } = useSelector((state) => state.auth);

  useEffect(() => {
    getUserPermissions(admin?.roles);
    if (admin?.data?.roleId != null) {
      localStorage.setItem("roleId", String(admin.data.roleId));
    } else {
      localStorage.removeItem("roleId");
    }
  }, [admin]);

  if (loadingUser) {
    return <LoadingModal />;
  }

  return (
    <PermissionProvider roles={admin?.roles} roleId={admin?.data?.roleId}>
      {isLoggedIn ? <MainRoutes /> : <AuthRoutes />}
    </PermissionProvider>
  );
};

const AuthRoutes = () => {
  return (
    <Routes>
      {/** Index route will redirect to mentioned initial route,
			 @element - initial route `string` in Navigate component */}
      <Route index element={<Navigate to={AUTH_ROUTES.SIGNIN} />} />

      <Route
        path={AUTH_ROUTES.SIGNIN}
        element={<WithSuspense load={<SignInScreen />} />}
      />
      <Route
        path={AUTH_ROUTES.RESET_PASS}
        element={<WithSuspense load={<ResetPassScreen />} />}
      />
      <Route
        path={AUTH_ROUTES.CHANGE_PASS}
        element={<WithSuspense load={<ChangePassScreen />} />}
      />
      <Route
        path={MAIN_ROUTES.PRIVACY_POLICY}
        element={<WithSuspense load={<PrivacyPolicyScreen />} />}
      />
      <Route
        path={MAIN_ROUTES.TERMS_CONDITIONS}
        element={<WithSuspense load={<TermsConditionsScreen />} />}
      />
      <Route
        path={MAIN_ROUTES.LOAN_LENDER}
        element={<WithSuspense load={<LoanLenderScreen />} />}
      />
      <Route
        path={MAIN_ROUTES.INSURANCE_LENDER}
        element={<WithSuspense load={<InsuranceLenderScreen />} />}
      />

      {/** Wildcard route will redirect all unhandled route to base route,
			 @element - base route `string` in Navigate component */}
      <Route path={"*"} element={<Navigate to={"/"} />} />
    </Routes>
  );
};

const MainRoutes = () => {
  return (
    <Routes>
      <Route path={"/"} element={<MainLayout />}>
        {/** Index route will redirect to mentioned initial route,
				 @element - initial route `string` in Navigate component */}
        <Route index element={<DefaultRedirect />} />

        <Route
          path={MAIN_ROUTES.DASHBOARD}
          element={<WithSuspense load={<DashboardScreen />} />}
        />
        <Route
          path={MAIN_ROUTES.PRIVACY_POLICY}
          element={<WithSuspense load={<PrivacyPolicyScreen />} />}
        />
        <Route
          path={MAIN_ROUTES.TERMS_CONDITIONS}
          element={<WithSuspense load={<TermsConditionsScreen />} />}
        />
        <Route
          path={MAIN_ROUTES.LOAN_LENDER}
          element={<WithSuspense load={<LoanLenderScreen />} />}
        />
        <Route
          path={MAIN_ROUTES.INSURANCE_LENDER}
          element={<WithSuspense load={<InsuranceLenderScreen />} />}
        />
        <Route
          path={MAIN_ROUTES.USER_AND_PERMISSION}
          element={<WithSuspense load={<UserAndPermissionsScreen />} />}
        />

        {/** Dealer management routes */}
        <Route path={MAIN_ROUTES.DEALER_MANAGEMENT.ROUTE}>
          <Route
            index
            element={<Navigate to={MAIN_ROUTES.DEALER_MANAGEMENT.DEALERS} />}
          />
          <Route
            path={MAIN_ROUTES.DEALER_MANAGEMENT.DEALERS}
            element={guarded(
              "Dealer List",
              "View",
              <WithSuspense load={<DealersScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.DEALER_MANAGEMENT.DEALER_SUBSCRIPTION}
            element={guarded(
              "Dealer Payments",
              "View",
              <WithSuspense load={<DealersSubscription />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.DEALER_MANAGEMENT.ADD_SUBSCRIPTION}
            element={guarded(
              "Dealer Payments",
              "View",
              <WithSuspense load={<AddDealerSubscription />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.DEALER_MANAGEMENT.NOTIFICATION}
            element={guarded(
              "Notification",
              "View",
              <WithSuspense load={<DealerNotificationScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.DEALER_MANAGEMENT.CALL_LOG_DASHBOARD}
            element={<WithSuspense load={<CallLogDashboard />} />}
          />
          <Route
            path={MAIN_ROUTES.DEALER_MANAGEMENT.NEW_DEALER}
            element={guarded(
              "Dealer List",
              "Add",
              <WithSuspense load={<NewDealerScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.DEALER_MANAGEMENT.EDIT_DEALER}
            element={guarded(
              "Dealer List",
              "Edit",
              <WithSuspense load={<NewDealerScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.DEALER_MANAGEMENT.VIEW_DEALER}
            element={guarded(
              "Dealer List",
              "View",
              <WithSuspense load={<ViewDealerScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.DEALER_MANAGEMENT.UPLOAD_DEALER}
            element={<WithSuspense load={<UploadDealer />} />}
          />
          <Route
            path={MAIN_ROUTES.DEALER_MANAGEMENT.BULK_ONBOARD_DEALER}
            element={guarded(
              "Dealer List",
              "Add",
              <WithSuspense load={<BulkOnboardDealer />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.DEALER_MANAGEMENT.BULK_ASSIGN_DEALER}
            element={guarded(
              "Dealer List",
              "Add",
              <WithSuspense load={<BulkAssignDealer />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.DEALER_MANAGEMENT.WHATSAPP_CHAT}
            element={guarded(
              "Dealer List",
              "View",
              <WithSuspense load={<DealerWhatsAppChat />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.DEALER_MANAGEMENT.DOCUMENT_UPLOAD}
            element={<WithSuspense load={<UploadDelaersDocument />} />}
          />
          <Route
            path={MAIN_ROUTES.DEALER_MANAGEMENT.DEALER_SETTING}
            element={<WithSuspense load={<DealerSetting />} />}
          />
          <Route
            path={MAIN_ROUTES.DEALER_MANAGEMENT.WEBSITE_HEADING}
            element={<WithSuspense load={<WebsiteHeading />} />}
          />
          <Route
            path={MAIN_ROUTES.DEALER_MANAGEMENT.DEALERSUBUSER}
            element={guarded(
              "Roles",
              "View",
              <WithSuspense load={<DealerSubScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.DEALER_MANAGEMENT.NEW_DEALERSUBUSER}
            element={guarded(
              "Roles",
              "Add",
              <WithSuspense load={<NewDealerSubScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.DEALER_MANAGEMENT.EDIT_DEALERSUBUSER}
            element={guarded(
              "Roles",
              "Edit",
              <WithSuspense load={<EditDealerSubScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.DEALER_MANAGEMENT.VIEW_DEALERSUBUSER}
            element={guarded(
              "Roles",
              "View",
              <WithSuspense load={<ViewDealerSubScreen />} />,
            )}
          />

          {/** Wildcard route will redirect all unhandled route to base route,
					 @element - base route `string` in Navigate component */}
          <Route
            path={"*"}
            element={<Navigate to={MAIN_ROUTES.DEALER_MANAGEMENT.ROUTE} />}
          />
        </Route>

        {/** Master management routes */}
        <Route path={MAIN_ROUTES.MASTER_MANAGEMENT.ROUTE}>
          {/** Index route will redirect to mentioned initial route,
					 @element - initial route `string` */}
          <Route
            index
            element={<Navigate to={MAIN_ROUTES.MASTER_MANAGEMENT.USERS} />}
          />

          <Route
            path={MAIN_ROUTES.MASTER_MANAGEMENT.USERS}
            element={guarded(
              "User",
              "View",
              <WithSuspense load={<UsersScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.MASTER_MANAGEMENT.NEW_USER}
            element={guarded(
              "User",
              "Add",
              <WithSuspense load={<NewUserScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.MASTER_MANAGEMENT.VIEW_USER}
            element={guarded(
              "User",
              "View",
              <WithSuspense load={<ViewUser />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.MASTER_MANAGEMENT.ROLES}
            element={guarded(
              "Roles",
              "View",
              <WithSuspense load={<RolesScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.MASTER_MANAGEMENT.NEW_ROLE}
            element={guarded(
              "Roles",
              "Add",
              <WithSuspense load={<NewRoleScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.MASTER_MANAGEMENT.EDIT_ROLE}
            element={guarded(
              "Roles",
              "Edit",
              <WithSuspense load={<NewRoleScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.MASTER_MANAGEMENT.VIEW_ROLE}
            element={guarded(
              "Roles",
              "View",
              <WithSuspense load={<ViewRole />} />,
            )}
          />

          {/** Wildcard route will redirect all unhandled route to base route,
					 @element - base route `string` in Navigate component */}
          <Route
            path={"*"}
            element={<Navigate to={MAIN_ROUTES.MASTER_MANAGEMENT.ROUTE} />}
          />
        </Route>

        {/**RTO Services routes */}
        <Route path={MAIN_ROUTES.RTO_SERVICES.ROUTE}>
          {/** Index route will redirect to mentioned initial route,
					 @element - initial route `string` */}

          <Route
            index
            element={
              <SectionDefaultRedirect
                sectionText="Services"
                fallback={MAIN_ROUTES.RTO_SERVICES.RTO_CASES}
              />
            }
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.PICKUP_REQUEST}
            element={guarded(
              "Door Service",
              "View",
              <WithSuspense load={<PickupRequestsScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.ADD_DOOR_SERVICE}
            element={guarded(
              "Door Service",
              "Add",
              <WithSuspense load={<AddDoorService />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.EDIT_DOOR_SERVICE}
            element={guarded(
              "Door Service",
              "Edit",
              <WithSuspense load={<EditDoorService />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.VIEW_DOOR_SERVICE}
            element={guarded(
              "Door Service",
              "View",
              <WithSuspense load={<ViewDoorService />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.RTO_CASES}
            element={guarded(
              "RTO Consultancy",
              "View",
              <WithSuspense load={<RTOCasesScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.ADD_RTO_CASE}
            element={guarded(
              "RTO Consultancy",
              "Add",
              <WithSuspense load={<NewRTOCasesScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.VIEW_RTO_CASE}
            element={guarded(
              "RTO Consultancy",
              "View",
              <WithSuspense load={<ViewRTOCasesScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.EDIT_RTO_CASE}
            element={guarded(
              "RTO Consultancy",
              "Edit",
              <WithSuspense load={<EditRTOCasesScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.CHALLAN}
            element={guarded(
              "Challan Check",
              "View",
              <WithSuspense load={<ChallanList />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.NEW_CHALLAN_REQUEST}
            element={guarded(
              "Challan Check",
              "Add",
              <WithSuspense load={<NewChallanRequest />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.ADD_CHALLAN}
            element={guarded(
              "Challan Check",
              "Add",
              <WithSuspense load={<NewChallan />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.EDIT_CHALLAN}
            element={guarded(
              "Challan Check",
              "Edit",
              <WithSuspense load={<EditChallan />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.VIEW_CHALLAN}
            element={guarded(
              "Challan Check",
              "View",
              <WithSuspense load={<ViewChallan />} />,
            )}
          />
          {/* Photoshoot */}
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.PHOTO_SHOOT}
            element={guarded(
              "Photoshoot Service",
              "View",
              <WithSuspense load={<PhotoShootScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.ADD_PHOTO_SHOOT}
            element={guarded(
              "Photoshoot Service",
              "Add",
              <WithSuspense load={<AddPhotoShootScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.EDIT_PHOTO_SHOOT}
            element={<WithSuspense load={<EditPhotoShootScreen />} />}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.VIEW_PHOTO_SHOOT}
            element={guarded(
              "Photoshoot Service",
              "View",
              <WithSuspense load={<ViewPhotoShootScreen />} />,
            )}
          />
          {/* Social Media */}
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.SOCIAL_MEDIA}
            element={guarded(
              "Social Media",
              "View",
              <WithSuspense load={<SocialMediaScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.ADD_SOCIAL_MEDIA}
            element={guarded(
              "Social Media",
              "Add",
              <WithSuspense load={<AddSocialMediaScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.EDIT_SOCIAL_MEDIA}
            element={guarded(
              "Social Media",
              "Edit",
              <WithSuspense load={<EditSocialMediaScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.VIEW_SOCIAL_MEDIA}
            element={guarded(
              "Social Media",
              "View",
              <WithSuspense load={<ViewSocialMediaScreen />} />,
            )}
          />

          {/* Website Development Media */}
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.WEBSITE_DEVELOPMENT}
            element={guarded(
              "Website Development",
              "View",
              <WithSuspense load={<WebsiteDevelopmentScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.ADD_WEBSITE_DEVELOPMENT}
            element={guarded(
              "Website Development",
              "Add",
              <WithSuspense load={<AddWebsiteDevelopmentScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.EDIT_WEBSITE_DEVELOPMENT}
            element={guarded(
              "Website Development",
              "Edit",
              <WithSuspense load={<EditWebsiteDevelopmentScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.VIEW_WEBSITE_DEVELOPMENT}
            element={guarded(
              "Website Development",
              "View",
              <WithSuspense load={<ViewWebsiteDevelopmentScreen />} />,
            )}
          />

          {/* Digital Marketing */}
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.ADD_DIGITAL_MARKETING}
            element={guarded(
              "Digital Marketing",
              "Add",
              <WithSuspense load={<NewDMScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.DIGITAL_MARKETING}
            element={guarded(
              "Digital Marketing",
              "View",
              <WithSuspense load={<ListDMScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.EDIT_DIGITAL_MARKETING}
            element={guarded(
              "Digital Marketing",
              "Edit",
              <WithSuspense load={<EditDMScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.VIEW_DIGITAL_MARKETING}
            element={guarded(
              "Digital Marketing",
              "View",
              <WithSuspense load={<ViewDMScreen />} />,
            )}
          />

          {/* Creative Marketing */}
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.CREATIVE_MAKING}
            element={guarded(
              "Creative Designing",
              "View",
              <WithSuspense load={<CreativeMakingScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.ADD_CREATIVE_MAKING}
            element={guarded(
              "Creative Designing",
              "Add",
              <WithSuspense load={<AddCreativeMakingScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.EDIT_CREATIVE_MAKING}
            element={guarded(
              "Creative Designing",
              "Edit",
              <WithSuspense load={<EditCreativeMakingScreenScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.VIEW_CREATIVE_MAKING}
            element={guarded(
              "Creative Designing",
              "View",
              <WithSuspense load={<ViewCreativeMakingScreen />} />,
            )}
          />

          {/* {Vahan Check} */}

          <Route
            path={MAIN_ROUTES.RTO_SERVICES.VIEW_VAHAN_CHECK}
            element={guarded(
              "Vahan Check",
              "View",
              <WithSuspense load={<ViewVahanCheck />} />,
            )}
          />

          <Route
            path={MAIN_ROUTES.RTO_SERVICES.VAHAN_CHECK}
            element={guarded(
              "Vahan Check",
              "View",
              <WithSuspense load={<VahanCheckListScreen />} />,
            )}
          />

          <Route
            path={MAIN_ROUTES.RTO_SERVICES.ADD_VAHAN_CHECK}
            element={guarded(
              "Vahan Check",
              "Check",
              <WithSuspense load={<AddVahanCheck />} />,
            )}
          />

          {/* D2D Auction*/}

          <Route
            path={MAIN_ROUTES.RTO_SERVICES.ADD_D2D_AUCTION}
            element={guarded(
              "Auction Services",
              "Add",
              <WithSuspense load={<ViewD2DScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.D2D_AUCTION}
            element={guarded(
              "Auction Services",
              "View",
              <WithSuspense load={<ListD2DScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.EDIT_D2D_AUCTION}
            element={guarded(
              "Auction Services",
              "Edit",
              <WithSuspense load={<ViewD2DScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.VIEW_D2D_AUCTION}
            element={guarded(
              "Auction Services",
              "View",
              <WithSuspense load={<ViewD2DScreen />} />,
            )}
          />

          {/* D2D Requirement*/}

          <Route
            path={MAIN_ROUTES.RTO_SERVICES.ADD_D2D_REQUIREMENT}
            element={guarded(
              "Auctions Requirement",
              "Add",
              <WithSuspense load={<ViewD2DRequirementScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.D2D_REQUIREMENT}
            element={guarded(
              "Auctions Requirement",
              "View",
              <WithSuspense load={<ListD2DRequirementScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.EDIT_D2D_REQUIREMENT}
            element={guarded(
              "Auctions Requirement",
              "Edit",
              <WithSuspense load={<ViewD2DRequirementScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.VIEW_D2D_REQUIREMENT}
            element={guarded(
              "Auctions Requirement",
              "View",
              <WithSuspense load={<ViewD2DRequirementScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.VIEW_DEALER_USER_REELS}
            element={<WithSuspense load={<DealerUserReels />} />}
          />
          <Route
            path={MAIN_ROUTES.RTO_SERVICES.CLASSIFIED_LISTING}
            element={guarded(
              "Classified Listing",
              "View",
              <WithSuspense load={<DealerClassifiedListing />} />,
            )}
          />

          <Route
            path={MAIN_ROUTES.RTO_SERVICES.ADD_CLASSIFIED_LISTING}
            element={guarded(
              "Classified Listing",
              "Add",
              <WithSuspense load={<DealerClassifiedListingAdd />} />,
            )}
          />

          <Route
            path={MAIN_ROUTES.RTO_SERVICES.EDIT_CLASSIFIED_LISTING}
            element={guarded(
              "Classified Listing",
              "Edit",
              <WithSuspense load={<DealerClassifiedListingEdit />} />,
            )}
          />

          <Route
            path={MAIN_ROUTES.RTO_SERVICES.VIEW_CLASSIFIED_LISTING}
            element={guarded(
              "Classified Listing",
              "View",
              <WithSuspense load={<DealerClassifiedListingView />} />,
            )}
          />

          {/** Wildcard route will redirect all unhandled route to base route,
					 @element - base route `string` in Navigate component */}
          <Route
            path={"*"}
            element={<Navigate to={MAIN_ROUTES.RTO_SERVICES.ROUTE} />}
          />
        </Route>

        {/**RTO Service History routes */}
        <Route path={MAIN_ROUTES.SERVICE_HISTORY.ROUTE}>
          {/** Index route will redirect to mentioned initial route,
           @element - initial route `string` */}

          <Route
            index
            element={<Navigate to={MAIN_ROUTES.SERVICE_HISTORY.SERVICE_LIST} />}
          />
          <Route
            path={MAIN_ROUTES.SERVICE_HISTORY.SERVICE_LIST}
            element={guarded(
              "Service History",
              "View",
              <WithSuspense load={<ServiceHistory />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.SERVICE_HISTORY.NEW_SERVICE}
            element={guarded(
              "Service History",
              "Add",
              <WithSuspense load={<NewService />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.SERVICE_HISTORY.VIEW_SERVICE}
            element={guarded(
              "Service History",
              "View",
              <WithSuspense load={<ViewService />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.SERVICE_HISTORY.EDIT_SERVICE}
            element={guarded(
              "Service History",
              "Edit",
              <WithSuspense load={<EditService />} />,
            )}
          />
          {/** Wildcard route will redirect all unhandled route to base route,
           @element - base route `string` in Navigate component */}
          <Route
            path={"*"}
            element={<Navigate to={MAIN_ROUTES.RTO_SERVICES.ROUTE} />}
          />
        </Route>

        {/* Vehicle Crime Report routes */}

        <Route path={MAIN_ROUTES.VEHICLE_CRIME.ROUTE}>
          {/** Index route will redirect to mentioned initial route,
           @element - initial route `string` */}

          <Route
            index
            element={
              <Navigate to={MAIN_ROUTES.VEHICLE_CRIME.VEHICLE_CRIME_LIST} />
            }
          />
          <Route
            path={MAIN_ROUTES.VEHICLE_CRIME.VEHICLE_CRIME_LIST}
            element={guarded(
              "Vehicle Crime Report",
              "View",
              <WithSuspense load={<VehicleCrime />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.VEHICLE_CRIME.NEW_SERVICE}
            element={guarded(
              "Vehicle Crime Report",
              "Add",
              <WithSuspense load={<NewVehicleCrime />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.VEHICLE_CRIME.VIEW_SERVICE}
            element={guarded(
              "Vehicle Crime Report",
              "View",
              <WithSuspense load={<ViewVehicleCrime />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.VEHICLE_CRIME.EDIT_SERVICE}
            element={guarded(
              "Vehicle Crime Report",
              "Edit",
              <WithSuspense load={<EditVehicleCrime />} />,
            )}
          />
          {/** Wildcard route will redirect all unhandled route to base route,
           @element - base route `string` in Navigate component */}
          <Route
            path={"*"}
            element={<Navigate to={MAIN_ROUTES.VEHICLE_CRIME.ROUTE} />}
          />
        </Route>

        {/** Transactions routes */}
        <Route path={MAIN_ROUTES.TRANSACTIONS.ROUTE}>
          {/** Index route will redirect to mentioned initial route,
					 @element - initial route `string` */}
          <Route
            index
            element={
              <Navigate to={MAIN_ROUTES.TRANSACTIONS.SERVICE_TRANSACTION} />
            }
          />
          <Route
            path={MAIN_ROUTES.TRANSACTIONS.SERVICE_TRANSACTION}
            element={guarded(
              "Service Transaction",
              "View",
              <WithSuspense load={<ServiceTransactionScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.TRANSACTIONS.BANK_TRANSACTION}
            element={guarded(
              "Bank Transaction",
              "View",
              <WithSuspense load={<BankTransactionScreen />} />,
            )}
          />

          <Route
            path={MAIN_ROUTES.TRANSACTIONS.ADD_BANK_TRANSACTION}
            element={guarded(
              "Bank Transaction",
              "Add",
              <WithSuspense load={<AddBankTransactionScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.TRANSACTIONS.OUTSTANDING_TRANSACTION}
            element={guarded(
              "Outstanding Transaction",
              "View",
              <WithSuspense load={<OutstandingTransactionScreen />} />,
            )}
          />

          {/** Wildcard route will redirect all unhandled route to base route,
					 @element - base route `string` in Navigate component */}
          <Route
            path={"*"}
            element={<Navigate to={MAIN_ROUTES.MASTER_MANAGEMENT.ROUTE} />}
          />
        </Route>

        {/** Inventory routes */}
        <Route path={MAIN_ROUTES.INVENTORY.ROUTE}>
          {/** Index route will redirect to mentioned initial route,
					 @element - initial route `string` */}
          <Route
            index
            element={<Navigate to={MAIN_ROUTES.INVENTORY.INVENTORY_LIST} />}
          />
          <Route
            path={MAIN_ROUTES.INVENTORY.ROUTE}
            element={<WithSuspense load={<InventoryScreen />} />}
          />
          <Route
            path={MAIN_ROUTES.INVENTORY.INVENTORY_LIST}
            element={<WithSuspense load={<InventoryListScreen />} />}
          />

          <Route
            path={MAIN_ROUTES.INVENTORY.NEW_INVENTORY}
            element={<WithSuspense load={<NewInventoryRequest />} />}
          />

          <Route
            path={MAIN_ROUTES.INVENTORY.EDIT_DETAILS}
            element={<WithSuspense load={<EditCarDetails />} />}
          />

          <Route
            path={MAIN_ROUTES.INVENTORY.UPLOAD_IMAGES}
            element={<WithSuspense load={<UpdateCarImages />} />}
          />

          {/** Wildcard route will redirect all unhandled route to base route,
					 @element - base route `string` in Navigate component */}
          <Route
            path={"*"}
            element={<Navigate to={MAIN_ROUTES.INVENTORY.ROUTE} />}
          />
        </Route>

        <Route path={MAIN_ROUTES.LEAD.ROUTE}>
          {/** Index route will redirect to mentioned initial route,
					 @element - initial route `string` */}
          <Route index element={<Navigate to={MAIN_ROUTES.LEAD.LEAD_LIST} />} />
          <Route
            path={MAIN_ROUTES.LEAD.LEAD_LIST}
            element={<WithSuspense load={<LeadListScreen />} />}
          />
          <Route
            path={MAIN_ROUTES.LEAD.CUSTOMER_LEAD}
            element={<WithSuspense load={<CustomerLead />} />}
          />
          <Route
            path={`${MAIN_ROUTES.LEAD.CUSTOMER_PREFERENCES}/:id`}
            element={<WithSuspense load={<CustomerPreferences />} />}
          />
          <Route
            path={MAIN_ROUTES.LEAD.ASSIGN_DEALERS}
            element={<WithSuspense load={<AssignDealers />} />}
          />
          {/* <Route
            path={MAIN_ROUTES.INVENTORY.INVENTORY_LIST}
            element={<WithSuspense load={<InventoryListScreen />} />}
          /> */}

          {/* <Route
            path={MAIN_ROUTES.INVENTORY.NEW_INVENTORY}
            element={<WithSuspense load={<NewInventoryRequest />} />}
          /> */}

          {/* <Route
            path={MAIN_ROUTES.INVENTORY.EDIT_DETAILS}
            element={<WithSuspense load={<EditCarDetails />} />}
          /> */}

          {/** Wildcard route will redirect all unhandled route to base route,
					 @element - base route `string` in Navigate component */}
          <Route
            path={"*"}
            element={<Navigate to={MAIN_ROUTES.LEAD.ROUTE} />}
          />
        </Route>

        {/** Financial Services routes */}
        <Route path={MAIN_ROUTES.FINANCIALSERVICES.ROUTE}>
          {/** Index route will redirect to mentioned initial route,
					 @element - initial route `string` */}
          <Route
            index
            element={<Navigate to={MAIN_ROUTES.FINANCIALSERVICES.LOANS_LIST} />}
          />
          <Route
            path={MAIN_ROUTES.FINANCIALSERVICES.ROUTE}
            element={<WithSuspense load={<FinancialServicesScreen />} />}
          />
          <Route
            path={MAIN_ROUTES.FINANCIALSERVICES.LOANS_LIST}
            element={guarded(
              "Customer Loans",
              "View",
              <WithSuspense load={<LoansListScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.FINANCIALSERVICES.ADD_LOAN}
            element={guarded(
              "Customer Loans",
              "Add",
              <WithSuspense load={<AddLoanScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.FINANCIALSERVICES.EDIT_LOAN}
            element={guarded(
              "Customer Loans",
              "Edit",
              <WithSuspense load={<EditLoanScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.FINANCIALSERVICES.VIEW_LOAN}
            element={guarded(
              "Customer Loans",
              "View",
              <WithSuspense load={<ViewLoanScreen />} />,
            )}
          />

          {/*  */}
          <Route
            path={MAIN_ROUTES.FINANCIALSERVICES.INSURANCE_LIST}
            element={guarded(
              "Customer Insurance",
              "View",
              <WithSuspense load={<InsuranceListScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.FINANCIALSERVICES.ADD_INSURANCE}
            element={guarded(
              "Customer Insurance",
              "Add",
              <WithSuspense load={<AddInsuranceScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.FINANCIALSERVICES.EDIT_INSURANCE}
            element={guarded(
              "Customer Insurance",
              "Edit",
              <WithSuspense load={<EditInsuranceScreen />} />,
            )}
          />
          <Route
            path={MAIN_ROUTES.FINANCIALSERVICES.VIEW_INSURANCE}
            element={guarded(
              "Customer Insurance",
              "View",
              <WithSuspense load={<ViewInsuranceScreen />} />,
            )}
          />

          {/** Wildcard route will redirect all unhandled route to base route,
					 @element - base route `string` in Navigate component */}
          <Route
            path={"*"}
            element={<Navigate to={MAIN_ROUTES.RTO_SERVICES.ROUTE} />}
          />
        </Route>

        <Route path={MAIN_ROUTES.CONTENT_MANAGEMENT.ROUTE}>
          {/** Index route will redirect to mentioned initial route,
					 @element - initial route `string` */}
          <Route
            path={MAIN_ROUTES.CONTENT_MANAGEMENT.CONTENT_LIST}
            element={<WithSuspense load={<ListDealerServiceImage />} />}
          />
          <Route
            path={MAIN_ROUTES.CONTENT_MANAGEMENT.ADD_CONTENT}
            element={<WithSuspense load={<AddDealerServiceImage />} />}
          />
          {/** Wildcard route will redirect all unhandled route to base route,
					 @element - base route `string` in Navigate component */}
          <Route
            path={"*"}
            element={<Navigate to={MAIN_ROUTES.CONTENT_MANAGEMENT.ROUTE} />}
          />
        </Route>

        {/** Wildcard route will redirect all unhandled route to the role's
				 first accessible route (not a hardcoded one it might not have
				 access to). */}
        <Route path={"*"} element={<DefaultRedirect />} />
      </Route>
    </Routes>
  );
};
